import {
  AttendanceCustomTimeRangeDayEnum,
  AttendanceEnumsPoolTypeEnum,
  PayrollConceptFragment,
  WorkTimeCategoriesCompensationsReimbursablePayrollConceptFragment,
  WorkTimeCategoriesContractTaxonomyFragment,
  WorkTimeCategoriesReimbursableSupplementTaxonomyFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { EXTRA_HOURS_RANGE_BASELINE } from 'modules/attendance/lib/enums'

export interface ReimbursableTaxonomiesMappingForm {
  id?: string
  supplementTaxonomyId: string
  legalEntityId: string
  timeRangeCategoryId: string
}

export type RangeType = 'fixed' | 'unlimited'

export interface CustomTimeRangesFormData {
  id?: string | undefined
  attendance_custom_time_range_category_id: string
  time_settings_custom_time_range_category_id: string
  day: AttendanceCustomTimeRangeDayEnum[]
  pool_type?: AttendanceEnumsPoolTypeEnum
  start_time?: string
  end_time?: string
  attendance_employees_setting_id: string
  reimbursable_taxonomies_mappings?: ReimbursableTaxonomiesMappingForm[]
}

export interface ExtraHoursRulesData {
  id?: string | undefined
  attendanceEmployeesSettingId: string
  poolType: AttendanceEnumsPoolTypeEnum
  rawMinutes: number
  equivalentMinutes: number
  fromHour: number
  toHour: number | undefined | null
  // Apply extra hours based on fixed (contract) or effective hours
  // Use shared enum for type‑safety across the page
  baseline?: EXTRA_HOURS_RANGE_BASELINE
  // For edit flow: keep the original baseline to control visibility
  // of the selector even if the feature flag is off until the user saves
  originalBaseline?: EXTRA_HOURS_RANGE_BASELINE
  errors?: {
    [id: string]: string
  }
  rangeType: RangeType
  canCreateSettingCategory: boolean
  timeSettingsCustomTimeRangeCategoryId?: number
  payrollEnabled: boolean
  contractTaxonomies: WorkTimeCategoriesContractTaxonomyFragment[]
  payrollConcepts?: PayrollConceptFragment[]
  formMappingsTaxonomies: ReimbursableTaxonomiesMappingForm[]
  payrollConceptId?: number | null
  payrollMappingsTaxonomies: WorkTimeCategoriesReimbursableSupplementTaxonomyFragment[]
  payrollMappingsCompensations?:
    | WorkTimeCategoriesCompensationsReimbursablePayrollConceptFragment[]
    | null
  hasCompensations?: boolean
  isEdit: boolean
  isDefault: boolean
  isUnlimited: () => boolean
}

export type ExtraHoursRulesDataField = keyof ExtraHoursRulesData

const validateMandatory = (field: ExtraHoursRulesDataField, data: ExtraHoursRulesData) => {
  const value = data[field]
  if (Array.isArray(value)) {
    return !!value.length
  }
  return !!data[field]
}

const getInvalidMandatoryFields = (
  mandatoryFields: ExtraHoursRulesDataField[],
  data: ExtraHoursRulesData
) => {
  return mandatoryFields
    .map((field) => {
      return validateMandatory(field, data)
        ? null
        : { [field]: i18n.t('time_tracking.settings.work_time_categories.forms.errors.mandatory') }
    })
    .filter(Boolean)
}

export const validateMandatoryFields = (
  mandatoryFields: ExtraHoursRulesDataField[],
  data: ExtraHoursRulesData
) => {
  const invalidFields = getInvalidMandatoryFields(mandatoryFields, data)

  const getDataWithErrors = () => {
    const errors = Object.assign({}, ...invalidFields)

    return {
      ...data,
      errors,
    }
  }
  return {
    dataWithErrors: getDataWithErrors(),
    invalidFields,
  }
}
